/*
  Module configuration file
 */
import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {ModuleConfiguration} from "../core/config/module-configuration";
import {TopicConfiguration} from "./topic-configuration";
import {FeatureLayerConfiguration} from "../core/config/layer-configuration";
import {LayerTemplate, PopupConfiguration} from "../core/config/popup-configuration";
import {LegendConfiguration} from "./legend-configuration";
import {SidebarConfiguration} from "./sidebar-configuration";
import {LayerListConfiguration} from "../core/config/layer-list-configuration";
import {WebMapConfiguration} from "../core/config/web-map-configuration";
import {
    FillSymbolConfiguration,
    PictureMarkerSymbolConfiguration
} from "../core/config/symbol-configuration";
import {MaintenanceConfiguration} from "../core/config/maintenance-configuration";
import {NavigationConfiguration} from "../config/navigation-configuration";
import {PrintConfiguration} from "../core/config/print-configuration";
import {ThematicInspectorConfiguration} from "../config/thematic-inspector-configuration";
import {MessageConfiguration} from "../core/config/message-configuration";
import {LinkConfiguration} from "../core/config/link-configuration";
import {ToolsConfiguration} from "../core/config/tools-configuration";
import {BookmarkConfiguration} from "../core/config/bookmark-configuration";
import {TopicSwitcherConfiguration} from "../core/config/topic-switcher-configuration";
import {CompareConfiguration} from "../core/config/compare-configuration";


@JsonObject()
export class WebMapMetaConfiguration extends ModuleConfiguration {
    @JsonProperty()
    declare alerts?: MessageConfiguration[];
    @JsonProperty()
    declare startupMessage?: MessageConfiguration;

    constructor() {
        super();
    }
}
@JsonObject()
export class TrackerConfiguration {
    @JsonProperty()
    declare urlBase: string;
    @JsonProperty()
    declare siteId: number;
    constructor() {
    }
}
@JsonObject()
export class MapViewerConfiguration extends ModuleConfiguration {
    @JsonProperty()
    declare bookmarks: BookmarkConfiguration[];

    @JsonProperty()
    declare breakpoints: {
        [key: string]: number;
    };
    @JsonProperty()
    declare compare: CompareConfiguration;

    // the topic activated on application's start
    @JsonProperty()
    declare initialTopic: string;

    /**
     * Layer list configuration
     */
    @JsonProperty()
    declare layerList: LayerListConfiguration;

    /**
     * Layer configurations (e.g. station layer)
     */
    @JsonProperty()
    declare layers: FeatureLayerConfiguration[];

    /**
     * "über diese Karte" Link
     */
    @JsonProperty()
    declare aboutLink?: LinkConfiguration;

    /**
     * "über diese Karte" Link
     */
    @JsonProperty()
    declare helpLink?: LinkConfiguration;
    /**
     * legend
     */
    @JsonProperty()
    declare legend: LegendConfiguration;

    /**
     * Printing
     */
    @JsonProperty()
    declare print: PrintConfiguration;

    /**
     * Information about maintenance work on the application
     */
    @JsonProperty({ type: MaintenanceConfiguration})
    declare maintenance?: MaintenanceConfiguration[];

    // Objektabfrage Popup
    @JsonProperty()
    declare popup: PopupConfiguration;

    @JsonProperty()
    declare tools: ToolsConfiguration;

    // Topics (Themen)
    @JsonProperty()
    declare topics: TopicConfiguration[];

    // Topics (Themen)
    @JsonProperty()
    declare subtopics?: TopicConfiguration[];

    @JsonProperty()
    declare topicSwitcher?: TopicSwitcherConfiguration;

    // Objektabfrage Highlight
    @JsonProperty()
    declare highlightSymbol: FillSymbolConfiguration;

    @JsonProperty()
    declare pointerSymbols?: HashMap<PictureMarkerSymbolConfiguration>;

    @JsonProperty()
    declare sidebar: SidebarConfiguration;

    @JsonProperty()
    declare tracker?: TrackerConfiguration;

    @JsonProperty()
    declare urls?: any;

    @JsonProperty()
    declare navigation: NavigationConfiguration;
    @JsonProperty()
    declare breadcrumbs: NavigationConfiguration;

    @JsonProperty()
    declare webMap: WebMapConfiguration;

    @JsonProperty()
    declare meta: WebMapMetaConfiguration;

    @JsonProperty()
    declare thematicInspector: ThematicInspectorConfiguration;
    @JsonProperty()
    declare htmlContent: HashMap<string | HashMap<string>>;
    @JsonProperty()
    declare objektluLayerList: HashMap<HashMap<string>>;
    @JsonProperty()
    declare adressen: HashMap<HashMap<string>>;
    /**
     * Enable or disable search field in locate button
     *
     * @default true
     */
    @JsonProperty()
    locateButtonEnabled = true;

    constructor() {
        super();
    }

    /**
     * Get topic configuration by ID
     *
     * @param topicId
     */
    getTopic(topicId: string): TopicConfiguration | undefined {
        return this.topics.find((topic) => topic.id === topicId);
    }

    /**
     * Get topic configuration by route
     *
     * @param route
     */
    getTopicByRoute(route: string, topics: TopicConfiguration[] = []): TopicConfiguration | undefined {
        if(topics.length === 0) {
            topics = this.topics;
        }
        return topics.find((topic) => topic.routes && topic.routes.includes(route));
    }
    /**
     * Get topic configuration by route
     *
     * @param route
     */
    getDefaultTopic(): TopicConfiguration | undefined {
        return this.topics[0];
    }

    /**
     * Get first selectable subtopic (used when a parent topic is selected)
     *
     * @param topic
     * @param topics optional
     */
    getFirstSubtopic(topic: TopicConfiguration, topics: TopicConfiguration[] = []): TopicConfiguration {
        if (!this.topics.length && !topics.length) {
            throw new Error("No topics configured.");
        }
        if(topics.length === 0) {
            topics = this.topics;
        }
        const firstSubtopic = topics.find((item) => item.parentId === topic.id && item.routes);
        if (!firstSubtopic) {
            throw new Error("No subtopic for id "+topic.id+" found");
        }

        return firstSubtopic;
    }

    /**
     * Get layer template
     * @param metauid
     * @param layerId
     */
    getLayerTemplate(metauid: string, layerId?: number): LayerTemplate | undefined {
        const layerTemplates: LayerTemplate[] | undefined = this.popup.layerTemplates;

        if (layerTemplates && metauid) {
            // `${metauid}-${layer.id}`
            let layerTemplate = layerTemplates.find((t: LayerTemplate) => {
                return t.metauid === metauid && (!t.layerIds || t.layerIds.length === 0 ||
                    (typeof layerId !== "undefined" && t.layerIds.includes(layerId)));
            });
            if (typeof layerTemplate?.template === "string") {
                layerTemplate = this.getLayerTemplate(layerTemplate.template);
            }
            return layerTemplate;
        }
        return;
    }
}

